// src/services/financeiroIntegracaoService.js
import { pb } from "./pocketbase";
import { logAction } from "./logService";

export const financeiroIntegracaoService = {
  // ─── REGRAS DE CATEGORIZAÇÃO ──────────────────────────────────────────
  async getRegrasCategorizacao() {
    try {
      return await pb.collection("fin_regras_categorizacao").getFullList({
        expand: "categoria_default,centro_custo_default",
      });
    } catch (err) {
      console.warn("Coleção fin_regras_categorizacao não existe ou erro ao buscar:", err.message);
      return [];
    }
  },

  async saveRegraCategorizacao(origem, categoriaId, centroId) {
    // Busca se já existe uma regra para esta origem
    let existing = null;
    try {
      const records = await pb.collection("fin_regras_categorizacao").getList(1, 1, {
        filter: `origem = "${origem}"`,
      });
      if (records.items.length > 0) {
        existing = records.items[0];
      }
    } catch (err) {
      console.warn("Erro ao buscar regra existente:", err.message);
    }

    const payload = {
      origem,
      categoria_default: categoriaId || null,
      centro_custo_default: centroId || null,
    };

    if (existing) {
      return await pb.collection("fin_regras_categorizacao").update(existing.id, payload);
    } else {
      return await pb.collection("fin_regras_categorizacao").create(payload);
    }
  },

  async deleteRegraCategorizacao(id) {
    try {
      await pb.collection("fin_regras_categorizacao").delete(id);
      return true;
    } catch (err) {
      console.error("[Financeiro Integracao] Erro ao deletar regra:", err.message);
      throw err;
    }
  },

  // ─── DISPARAR NOVA PENDÊNCIA (RECEITA / DESPESA) ──────────────────────
  async triggerPendingTransaction({
    origem,
    origemId,
    snapshot,
    dataSugerida,
    valorSugeridoCentavos,
    descricaoSugerida,
    tipoPendencia = "entrada",
  }) {
    try {
      // 1. Deduplicação e Idempotência:
      // Verifica se já existe uma pendência para essa origem + origemId
      const pCount = await pb.collection("fin_lancamentos_pendentes").getList(1, 1, {
        filter: `origem = "${origem}" && origem_id = "${origemId}" && tipo_pendencia = "${tipoPendencia}"`,
      });
      if (pCount.items.length > 0) {
        console.log(`[Financeiro Integracao] Pendência duplicada ignorada: ${origem} - ${origemId}`);
        return { record: pCount.items[0], created: false };
      }

      // Verifica se já existe um lançamento no Ledger
      const lCount = await pb.collection("fin_lancamentos").getList(1, 1, {
        filter: `origem = "${origem}" && origem_id = "${origemId}"`,
      });
      if (lCount.items.length > 0) {
        console.log(`[Financeiro Integracao] Lançamento já consolidado ignorado: ${origem} - ${origemId}`);
        return { record: null, created: false };
      }

      // 1.1 Deduplicação cruzada pelo número do pedido (para production_orders, reservations, pdv_sales)
      let orderNum = null;
      if (origem === "production_orders" || origem === "producao_orders") {
        orderNum = snapshot?.order_number || snapshot?.full_data?.order?.number || snapshot?.full_data?.orderNumber;
      } else if (origem === "reservations") {
        orderNum = snapshot?.order_metadata?.number || snapshot?.order?.number || snapshot?.sku;
      } else if (origem === "pdv_sales") {
        orderNum = snapshot?.order_number;
      }

      if (orderNum && orderNum !== "S/N" && orderNum !== "AVULSO" && String(orderNum).trim() !== "") {
        const queryOrderNum = String(orderNum).trim();
        
        // Verifica se já existe pendência contendo este número de pedido na descrição sugerida
        const pNumCount = await pb.collection("fin_lancamentos_pendentes").getList(1, 1, {
          filter: `descricao_sugerida ~ "${queryOrderNum}"`,
        });
        if (pNumCount.items.length > 0) {
          console.log(`[Financeiro Integracao] Pendência duplicada por número de pedido ${queryOrderNum} ignorada.`);
          return { record: pNumCount.items[0], created: false };
        }

        // Verifica se já existe lançamento consolidado contendo este número de pedido na descrição
        const lNumCount = await pb.collection("fin_lancamentos").getList(1, 1, {
          filter: `descricao ~ "${queryOrderNum}"`,
        });
        if (lNumCount.items.length > 0) {
          console.log(`[Financeiro Integracao] Lançamento já consolidado por número de pedido ${queryOrderNum} ignorado.`);
          return { record: null, created: false };
        }
      }

      // 2. Mapeamento de regras padrão
      let categoriaSugerida = null;
      let centroCustoSugerido = null;
      try {
        if (origem === "supply_purchases") {
          // Resolução dinâmica de regras baseada nos itens do lote
          const resolved = await this.resolveSupplyPurchaseRules(snapshot);
          categoriaSugerida = resolved.categoryId;
          centroCustoSugerido = resolved.centroId;
        } else {
          const rules = await pb.collection("fin_regras_categorizacao").getList(1, 1, {
            filter: `origem = "${origem}"`,
          });
          if (rules.items.length > 0) {
            categoriaSugerida = rules.items[0].categoria_default || null;
            centroCustoSugerido = rules.items[0].centro_custo_default || null;
          }
        }
      } catch (err) {
        console.warn("Erro ao ler regras de categorização:", err.message);
      }

      // 3. Criar pendência no Staging ou Lançamento direto no Ledger (R4)
      const isVenda = origem === "production_orders" || origem === "producao_orders" || origem === "pdv_sales" || origem === "reservations";
      
      if (isVenda) {
        const lancamento = await pb.collection("fin_lancamentos").create({
          data: dataSugerida,
          descricao: descricaoSugerida,
          valor_centavos: valorSugeridoCentavos,
          categoria: categoriaSugerida,
          centro_custo: centroCustoSugerido,
          status: "pendente_conciliacao",
          origem,
          origem_id: origemId,
          observacoes: "Lançamento automático de receita de venda."
        });
        console.log(`[Financeiro Integracao] Lançamento de venda direto gerado no ledger: ${lancamento.id}`);
        return { record: lancamento, created: true };
      } else {
        const pendencia = await pb.collection("fin_lancamentos_pendentes").create({
          origem,
          origem_id: origemId,
          tipo_pendencia: tipoPendencia,
          snapshot,
          categoria_sugerida: categoriaSugerida,
          centro_custo_sugerido: centroCustoSugerido,
          valor_sugerido_centavos: valorSugeridoCentavos,
          data_sugerida: dataSugerida,
          descricao_sugerida: descricaoSugerida,
          status: "pendente",
        });
        console.log(`[Financeiro Integracao] Nova pendência gerada: ${pendencia.id}`);
        return { record: pendencia, created: true };
      }
    } catch (err) {
      console.error("[Financeiro Integracao] Erro ao disparar pendência:", err.message);
      return { record: null, created: false };
    }
  },

  // ─── CANCELAMENTO OU ESTORNO ──────────────────────────────────────────
  async triggerCancellation(origem, origemId, currentUser) {
    try {
      let orderNum = null;
      let reservationId = null;

      // Se for cancelamento de Pedido de Fábrica, tenta correlacionar com a Reserva original
      if (origem === "production_orders" || origem === "producao_orders") {
        try {
          const ord = await pb.collection("production_orders").getOne(origemId);
          if (ord) {
            orderNum = ord.order_number || ord.full_data?.order?.number;
            reservationId = ord.full_data?.originalReservationId;
          }
        } catch (e) {
          console.warn("Erro ao ler dados do pedido para cancelamento:", e.message);
        }
      }

      // Constrói filtro dinâmico de pendências
      let filterPend = `(origem = "${origem}" && origem_id = "${origemId}" && status = "pendente")`;
      if (reservationId) {
        filterPend += ` || (origem = "reservations" && origem_id = "${reservationId}" && status = "pendente")`;
      }
      if (orderNum && orderNum !== "S/N" && orderNum !== "AVULSO") {
        filterPend += ` || (origem = "reservations" && descricao_sugerida ~ "${orderNum}" && status = "pendente")`;
      }

      // 1. Localizar pendências em aberto (status = pendente)
      const pendenciasAbertas = await pb.collection("fin_lancamentos_pendentes").getFullList({
        filter: filterPend,
      });

      for (const pend of pendenciasAbertas) {
        // Auto-rejeita pendência que ainda não foi confirmada pelo operador
        await pb.collection("fin_lancamentos_pendentes").update(pend.id, {
          status: "rejeitado",
          revisado_por: currentUser?.id || null,
          data_revisao: new Date().toISOString(),
          motivo_rejeicao: "Cancelado na origem antes da consolidação",
        });
        await logAction(
          currentUser,
          "FINANCEIRO",
          "PENDENCIA_AUTO_REJEITADA",
          `Pendência ID ${pend.id} auto-rejeitada devido a cancelamento operacional de ${origem}:${origemId}`
        );
      }

      // Constrói filtro dinâmico de lançamentos
      let filterLanc = `(origem = "${origem}" && origem_id = "${origemId}" && tipo_lancamento != "estorno")`;
      if (reservationId) {
        filterLanc += ` || (origem = "reservations" && origem_id = "${reservationId}" && tipo_lancamento != "estorno")`;
      }
      if (orderNum && orderNum !== "S/N" && orderNum !== "AVULSO") {
        filterLanc += ` || (origem = "reservations" && descricao ~ "${orderNum}" && tipo_lancamento != "estorno")`;
      }

      // 2. Se já foi confirmada (existe lançamento em fin_lancamentos), gera pendência de estorno
      const lancamentosConsolidados = await pb.collection("fin_lancamentos").getFullList({
        filter: filterLanc,
      });

      for (const lanc of lancamentosConsolidados) {
        // Verifica se já existe um estorno pendente ou confirmado para esse lançamento
        const estornosPendentes = await pb.collection("fin_lancamentos_pendentes").getList(1, 1, {
          filter: `origem = "${lanc.origem}" && origem_id = "${lanc.origem_id}" && tipo_pendencia = "estorno"`,
        });
        const estornosConfirmados = await pb.collection("fin_lancamentos").getList(1, 1, {
          filter: `referencia_estorno_de = "${lanc.id}" && tipo_lancamento = "estorno"`,
        });

        if (estornosPendentes.items.length === 0 && estornosConfirmados.items.length === 0) {
          // Cria uma pendência do tipo estorno
          const pendEstorno = await pb.collection("fin_lancamentos_pendentes").create({
            origem: lanc.origem,
            origem_id: lanc.origem_id,
            tipo_pendencia: "estorno",
            snapshot: {
              motivo: `Estorno automático de cancelamento de ${origem}`,
              lancamento_original_id: lanc.id,
            },
            categoria_sugerida: lanc.categoria,
            centro_custo_sugerido: lanc.centro_custo,
            valor_sugerido_centavos: -lanc.valor_centavos, // Inverte o sinal do valor original
            data_sugerida: new Date().toISOString().split("T")[0],
            descricao_sugerida: `Estorno de ${lanc.descricao}`,
            status: "pendente",
          });
          console.log(`[Financeiro Integracao] Pendência de estorno gerada: ${pendEstorno.id}`);
        }
      }
    } catch (err) {
      console.error("[Financeiro Integracao] Erro ao tratar cancelamento:", err.message);
    }
  },

  async updateOrigemOnConversion(oldOrigem, oldOrigemId, newOrigem, newOrigemId) {
    try {
      // 1. Procura na fila de pendências
      const pendencias = await pb.collection("fin_lancamentos_pendentes").getFullList({
        filter: `origem = "${oldOrigem}" && origem_id = "${oldOrigemId}"`
      });
      for (const pend of pendencias) {
        await pb.collection("fin_lancamentos_pendentes").update(pend.id, {
          origem: newOrigem,
          origem_id: newOrigemId
        });
        console.log(`[Financeiro Integracao] Atualizada origem da pendência ${pend.id} de ${oldOrigem} para ${newOrigem}`);
      }

      // 2. Procura no Ledger final
      const lancamentos = await pb.collection("fin_lancamentos").getFullList({
        filter: `origem = "${oldOrigem}" && origem_id = "${oldOrigemId}"`
      });
      for (const lanc of lancamentos) {
        await pb.collection("fin_lancamentos").update(lanc.id, {
          origem: newOrigem,
          origem_id: newOrigemId
        });
        console.log(`[Financeiro Integracao] Atualizada origem do lançamento ledger ${lanc.id} de ${oldOrigem} para ${newOrigem}`);
      }
    } catch (err) {
      console.warn("[Financeiro Integracao] Erro ao atualizar origem na conversão:", err.message);
    }
  },

  async resolveSupplyPurchaseRules(purchase) {
    let bestCategory = null;
    let bestCentro = null;

    try {
      // 1. Busca todas as regras de categorização para supply_purchases
      const allRules = await pb.collection("fin_regras_categorizacao").getFullList({
        filter: `origem ~ "supply_purchases"`,
      });

      // Cria mapeamentos rápidos
      const categoryRules = {};
      const sectorRules = {};
      let defaultRule = null;

      for (const rule of allRules) {
        if (rule.origem === "supply_purchases") {
          defaultRule = rule;
        } else if (rule.origem.startsWith("supply_purchases:category:")) {
          const catName = rule.origem.replace("supply_purchases:category:", "").toLowerCase().trim();
          categoryRules[catName] = rule;
        } else if (rule.origem.startsWith("supply_purchases:sector:")) {
          const secName = rule.origem.replace("supply_purchases:sector:", "").toLowerCase().trim();
          sectorRules[secName] = rule;
        }
      }

      // 2. Coleta os IDs únicos de insumos e busca todos de uma única vez no PocketBase (N+1 Query Optimization)
      const items = purchase?.items || [];
      const uniqueSupplyIds = [...new Set(items.map(it => it.supply_id).filter(Boolean))];
      
      const suppliesMap = new Map();
      if (uniqueSupplyIds.length > 0) {
        try {
          const chunks = [];
          // Divide em grupos de 30 IDs para evitar filtros excessivamente longos
          for (let i = 0; i < uniqueSupplyIds.length; i += 30) {
            chunks.push(uniqueSupplyIds.slice(i, i + 30));
          }

          for (const chunk of chunks) {
            const filterStr = chunk.map(id => `id = "${id}"`).join(" || ");
            const suppliesList = await pb.collection("supplies").getFullList({ filter: filterStr });
            for (const s of suppliesList) {
              suppliesMap.set(s.id, s);
            }
          }
        } catch (e) {
          console.warn("[Financeiro Integracao] Erro ao buscar lista de insumos otimizada:", e.message);
        }
      }

      const categoryValues = {}; // key: "catId:centroId", value: total centavos
      let calculatedTotalCentavos = 0;

      for (const it of items) {
        const qty = it.ordered_quantity || it.received_quantity || it.qty_ordered || it.quantity || 0;
        
        let itemCategory = "";
        let itemSector = "";
        let itemCostReais = 0;

        if (it.supply_id) {
          const supply = suppliesMap.get(it.supply_id);
          if (supply) {
            itemCategory = String(supply.category || "").toLowerCase().trim();
            itemSector = String(supply.sector || "").toLowerCase().trim();
            itemCostReais = Number(supply.custo_unidade_compra) || (Number(supply.custo_unitario) * (Number(supply.unidades_por_compra) || 1)) || 0;
          }
        }

        const itemValCents = Math.round(qty * itemCostReais * 100);
        calculatedTotalCentavos += itemValCents;

        // Tenta resolver a regra: primeiro categoria específica, depois setor, depois default
        let resolvedRule = categoryRules[itemCategory] || sectorRules[itemSector] || defaultRule;

        if (resolvedRule) {
          const catId = resolvedRule.categoria_default || "none";
          const centroId = resolvedRule.centro_custo_default || "none";
          const key = `${catId}:${centroId}`;
          categoryValues[key] = (categoryValues[key] || 0) + itemValCents;
        }
      }

      // Encontra a chave com maior valor financeiro acumulado
      let maxVal = -1;
      let bestKey = null;
      for (const [key, val] of Object.entries(categoryValues)) {
        if (val > maxVal) {
          maxVal = val;
          bestKey = key;
        }
      }

      if (bestKey) {
        const [catId, centroId] = bestKey.split(":");
        bestCategory = catId !== "none" ? catId : null;
        bestCentro = centroId !== "none" ? centroId : null;
      }
    } catch (err) {
      console.warn("[Financeiro Integracao] Erro ao resolver regras para compra de insumos:", err.message);
    }

    return { categoryId: bestCategory, centroId: bestCentro, totalCentavos: calculatedTotalCentavos };
  },
};
